import { type JSX, useEffect, useRef } from 'react';

import {
  DataTableIsHeaderContextProvider,
  useDataTable,
} from '../contexts/DataTableContext.js';
import { ColumnOrderDragAndDropContext } from '../features/ColumnOrder/ColumnOrderDragAndDropContext.js';
import * as headerStyles from './DataTableHeader.sty.js';
import { DataTableHeaderCell } from './DataTableHeaderCell.js';

export function DataTableHeader(): JSX.Element {
  const table = useDataTable();
  const headerGroups = table?.getHeaderGroups();
  const headerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries.at(0)?.borderBoxSize?.at(0)?.blockSize;
      table.setObservedHeaderHeight(height ?? 0);
    });
    resizeObserver.observe(headerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [table]);

  const headerRows = headerGroups.map((headerGroup, rowIndex, rows) => {
    const headers = table.getOrderedHeaderGroups()[rowIndex];
    const headerCells = headers.map((header, columnIndex) => {
      return (
        <DataTableHeaderCell
          key={
            header.subHeaders.length > 0
              ? `${header.column.id}_${header.subHeaders[0].column.id}`
              : header.column.id
          }
          header={header}
          headers={headers}
          headerGroupId={headerGroup.id}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          lastRowIndex={rows.length - 1}
        />
      );
    });

    return (
      <div role="row" className={headerStyles.headerRow} key={headerGroup.id}>
        {headerCells}
      </div>
    );
  });

  const headerContent = table.options.enableColumnOrdering ? (
    <ColumnOrderDragAndDropContext>{headerRows}</ColumnOrderDragAndDropContext>
  ) : (
    // If column ordering is not enabled, don't use the ColumnOrderDragAndDropContext
    // oxlint-disable-next-line react/jsx-no-useless-fragment
    <>{headerRows}</>
  );

  return (
    <DataTableIsHeaderContextProvider>
      <div
        ref={headerRef}
        role="rowgroup"
        data-rowgroup="header"
        className={headerStyles.header}
      >
        {headerContent}
      </div>
    </DataTableIsHeaderContextProvider>
  );
}

DataTableHeader.displayName = 'DataTableHeader';
